import { useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: true });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-8 sm:p-10">
          <div className="text-center mb-8">
            <GraduationCap className="w-10 h-10 text-cabernet mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-medium text-cabernet mb-2">
              {sent ? 'Check Your Email' : 'Forgot Password'}
            </h1>
            <p className="font-body text-sm text-ink/60">
              {sent
                ? 'If an account exists with that email, we sent a password reset link.'
                : 'Enter your email and we\'ll send you a reset link.'}
            </p>
          </div>

          {sent ? (
            <div className="text-center">
              <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-4" />
              <p className="font-body text-sm text-ink/60 mb-6" data-testid="forgot-success-message">
                Check your inbox (and spam folder) for the reset link. The link expires in 1 hour.
              </p>
              <Link to="/login">
                <Button variant="outline" className="border-2 border-cabernet text-cabernet hover:bg-cabernet/5 font-accent text-sm" data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-3">
                {error && (
                  <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm font-accent text-red-700" data-testid="forgot-error">
                    {error}
                  </div>
                )}
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-cream border-cabernet/20 focus:ring-brass font-body"
                  data-testid="forgot-email-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm py-5"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>

              <p className="text-center text-sm font-body text-ink/60 mt-5">
                <Link to="/login" className="text-cabernet font-semibold hover:underline" data-testid="back-to-login-link">
                  <ArrowLeft className="w-3 h-3 inline mr-1" />
                  Back to Sign In
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
